import { DndContext, DragOverlay } from '@dnd-kit/core'
import { type Context, type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

import { StoryListElement } from '@/components/StoryListElement.tsx'
import { useDragAndDropSettings } from '@/components/useDragAndDropSettings.ts'
import { useSubscription } from '@/hooks/useSubscription.ts'
import { getStory } from '@/requests/apollo/cache.ts'
import { useMoveStory } from '@/requests/mutations/useMoveStory.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import { getGroupName } from '@/utils/status.ts'
import { submitResidualData } from '@/utils/story.ts'

type StoryInteractionContextType = {
  selectedStoryIds: Set<string>
  addSelectedStoryIds: (ids: string[]) => void
  removeSelectedStoryIds: (ids: string[]) => void
  clearSelectedStoryIds: () => void
  selectedGroupName: string | null
  getIsStoryFormOpen: (storyId: string) => boolean
  toggleStoryFormOpen: (storyId: string) => void
}

export const StoryInteractionContext = createContext<StoryInteractionContextType | null>(null)

type Props = {
  children: ReactNode
  isSortable?: boolean
  isSelectable?: boolean
}

export const StoryInteractionProvider = ({ children, isSortable }: Props) => {
  const { sensors } = useDragAndDropSettings()
  const [selectedStoryIds, setSelectedStoryIds] = useState<Set<string>>(new Set())
  const [openedStoryIds, setOpenedStoryIds] = useState<Set<string>>(new Set())
  const [activeId, setActiveId] = useState<string | null>(null)
  const { moveStories } = useMoveStory()

  const selectedGroupName = useMemo(() => {
    const firstEntry = selectedStoryIds.values().next().value
    if (!firstEntry) return null
    const story = getStory(firstEntry)
    return story?.status ? getGroupName(story.status).group : null
  }, [selectedStoryIds])

  const addSelectedStoryIds = useCallback(
    (ids: string[]) => setSelectedStoryIds((prev) => new Set([...prev, ...ids])),
    [],
  )

  const removeSelectedStoryIds = useCallback((ids: string[]) => {
    setSelectedStoryIds((prev) => {
      const newSelectedStoryIds = new Set(prev)
      for (const id of ids) newSelectedStoryIds.delete(id)
      return newSelectedStoryIds
    })
  }, [])
  const clearSelectedStoryIds = useCallback(() => setSelectedStoryIds(new Set()), [])
  const getIsStoryFormOpen = useCallback((storyId: string) => openedStoryIds.has(storyId), [openedStoryIds])
  const toggleStoryFormOpen = useCallback((storyId: string, selected?: boolean) => {
    setOpenedStoryIds((prev) => {
      const newSet = new Set(prev)
      switch (true) {
        case selected === undefined && !newSet.has(storyId):
        case selected: {
          newSet.add(storyId)
          break
        }
        default: {
          submitResidualData(storyId)
          newSet.delete(storyId)
        }
      }
      return newSet
    })
  }, [])

  useSubscription([
    [GlobalPubSub.Signals.DESELECT_STORIES, removeSelectedStoryIds as Callback],
    [GlobalPubSub.Signals.TOGGLE_STORY_FORM, toggleStoryFormOpen as Callback],
  ])

  return (
    <StoryInteractionContext.Provider
      value={{
        selectedStoryIds,
        addSelectedStoryIds,
        removeSelectedStoryIds,
        clearSelectedStoryIds,
        selectedGroupName,
        getIsStoryFormOpen,
        toggleStoryFormOpen,
      }}
    >
      {isSortable ? (
        <DndContext
          sensors={sensors}
          onDragStart={({ active }) => {
            setActiveId(active.id as string)
          }}
          onDragEnd={({ active, over }) => {
            const activeId = active?.id as string
            const overId = over?.id as string

            if (!activeId || !overId) return

            moveStories(
              selectedStoryIds.has(activeId) ? Array.from(selectedStoryIds) : [activeId],
              overId,
              activeId,
            ).then(() => {
              setActiveId(null)
              clearSelectedStoryIds()
            })
          }}
        >
          {children}
          {activeId && (
            <DragOverlay dropAnimation={null}>
              <div className="opacity-45">
                {/* biome-ignore lint/style/noNonNullAssertion: This guaranteed to exist. */}
                <StoryListElement story={getStory(activeId)!} />
              </div>
            </DragOverlay>
          )}
        </DndContext>
      ) : (
        children
      )}
    </StoryInteractionContext.Provider>
  )
}

export const useStoryInteractionContext = () =>
  useContext(StoryInteractionContext as Context<StoryInteractionContextType>)
